import { Navigate, Outlet, RouteObject } from 'react-router-dom';
import {
  ALL_ACCESS,
  canAccessEditor,
  canAccessPeople,
  canAccessRtmp,
  DEVELOPER_ONLY,
  EPermissions,
} from '@/types/permissions.types';
import { ICONS } from '@ui/icons/types';
import EditProjectErrorBoundary from '../grid-areas/main/edit-project-error-boundary/edit-project-error-boundary';
import MainContentWrapper from '../../shared-create-and-edit-components/grid-areas/main/project-overview/main-content-wrapper';
import RouteGuard from '@ui/route-guard/route-guard';
import ProjectOverview from '../grid-areas/main/project-overview/project-overview';
import ProjectSession from '../grid-areas/main/project-overview/project-session';
import { getBaseAdminURL } from '@/utils/urls';
import Settings from '../grid-areas/main/settings/settings';
import ComingSoon from '@/micros/coming-soon/coming-soon';
// import StatsCleanupWrapper from '../grid-areas/main/analytics/stats-cleanup-wrapper.tsx';
import CastAndCrew from '../grid-areas/main/cast-and-crew/cast-and-crew.tsx';
import Stylepacks from '../grid-areas/main/stylepacks/stylepacks.tsx';
import { stylepacksLoader } from '../grid-areas/main/stylepacks/stylepacks.loader.ts';
import BrowseStylepacks from '../grid-areas/main/stylepacks/browse/browse-stylepacks.tsx';
import PreviewStylepack from '../grid-areas/main/stylepacks/preview/preview-stylepack.tsx';
import Audiences from '../grid-areas/main/audiences/audiences.tsx';
import AudiencesList from '../grid-areas/main/audiences/components/listsv2/audiences-list.tsx';
import Registrants from '../grid-areas/main/audiences/components/registrants/audiences-registrants.tsx';
import { AUDIENCES_VIEW } from '@/types/registration.types.ts';
import Campaigns from '../grid-areas/main/campaigns/campaigns.tsx';
import CampaignEditor from '../grid-areas/main/campaigns/editor/campaign-editor.tsx';
import AllProfiles from '../grid-areas/main/audiences/components/listsv2/all-profiles.tsx';
import AudiencesRouteGate from '../grid-areas/main/audiences/audiences-route-gate.tsx';

export type EditProjectRouteConfig = RouteObject & {
  id: string;
  link_text: string;
  allowedRoles?: EPermissions[];
  blockedRoles?: EPermissions[];
  redirectTo?: string;
  onClick?: () => void; // For links that are functions that check some stuff and then redirect
  unauthorizedMessage?: string;
  icon?: string;
  isIndex?: boolean;
  notAvailable?: boolean;
};

// Define simplified permission configurations
// Note: owner/admin/builder roles by default are allowed access (see use-has-permission.ts)
const ROUTE_PERMISSIONS = {
  // Admin-only routes
  adminOnly: {
    allowedRoles: [],
    unauthorizedMessage:
      'Sorry, you don’t have permission to view this content. If you think this is a mistake, please contact your admin.',
    redirectTo: '/project/edit/:projectUuid/:language', // Redirect to the parent route (edit-project)
  },
  // General access routes (Editor, Analytics, Producer, GroupAdmin)
  generalAccess: {
    allowedRoles: [EPermissions.GroupAdmin, EPermissions.Editor, EPermissions.Moderation, EPermissions.Producer],
    unauthorizedMessage:
      'Sorry, you don’t have permission to view this content. If you think this is a mistake, please contact your admin.',
    redirectTo: getBaseAdminURL(),
  },
  editor: {
    allowedRoles: [EPermissions.Editor],
    unauthorizedMessage:
      'Sorry, you don’t have permission to view this content. If you think this is a mistake, please contact your admin.',
    redirectTo: getBaseAdminURL(),
  },
  developer: {
    allowedRoles: [EPermissions.Editor, EPermissions.Builder, EPermissions.GroupAdmin],
    unauthorizedMessage:
      'Sorry, you don’t have permission to view this content. If you think this is a mistake, please contact your admin.',
    redirectTo: '/project/edit/:projectUuid/:language',
  },
};

export const EDIT_PROJECT_ROUTES = new Map([
  ['edit-project-cast-and-crew', canAccessEditor],
  ['edit-project-calendar', ALL_ACCESS],
  ['edit-project-tasks', ALL_ACCESS],
  ['edit-project-audiences', canAccessPeople],
  ['edit-project-campaigns', DEVELOPER_ONLY],
  ['edit-project-content', ALL_ACCESS],
  ['edit-project-live-monitor', ALL_ACCESS],
  // 'edit-project-stats',
]);

export const EDIT_PROJECT_ROUTES_COMING_SOON = new Map([
  ['edit-project-creative-boards', ALL_ACCESS],
  ['edit-project-stylepacks', canAccessRtmp],
  ['edit-project-run-of-show', ALL_ACCESS],
  ['edit-project-segments', canAccessRtmp],
  ['edit-project-scripts', ALL_ACCESS],
  ['edit-project-slides', ALL_ACCESS],
]);

export const EDIT_PROJECT_ROUTES_CONFIG: EditProjectRouteConfig[] = [
  {
    id: 'edit-project-overview',
    link_text: 'Overview',
    path: '',
    isIndex: true,
    icon: ICONS.HOME2,
    ...ROUTE_PERMISSIONS.generalAccess,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project overview." />,
    element: (
      <MainContentWrapper>
        <ProjectOverview />
      </MainContentWrapper>
    ),
  },
  {
    id: 'edit-project-session',
    link_text: 'Session',
    path: 'session/:sessionUuid/*',
    icon: ICONS.INFO_CIRCLE,
    ...ROUTE_PERMISSIONS.generalAccess,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project session." />,
    element: (
      <MainContentWrapper>
        <ProjectSession />
      </MainContentWrapper>
    ),
  },
  {
    id: 'edit-project-agenda',
    link_text: 'Agenda',
    path: 'agenda/*',
    icon: ICONS.CALENDAR2,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project agenda." />,
    element: (
      <MainContentWrapper>
        <ComingSoon label="Agenda" />
      </MainContentWrapper>
    ),
    notAvailable: true,
  },
  {
    id: 'edit-project-cast-and-crew',
    link_text: 'Cast & Crew',
    path: 'cast-and-crew/*',
    icon: ICONS.PEOPLE_NAV2,
    ErrorBoundary: () => (
      <EditProjectErrorBoundary label="Oops! There was an error loading your project cast and crew." />
    ),
    element: (
      <MainContentWrapper>
        <CastAndCrew />
      </MainContentWrapper>
    ),
  },
  {
    id: 'edit-project-calendar',
    link_text: 'Calendar',
    path: 'calendar/*',
    icon: ICONS.CALENDAR2,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project calendar." />,
    element: (
      <MainContentWrapper>
        <ComingSoon label="Calendar" />
      </MainContentWrapper>
    ),
    notAvailable: true,
  },
  {
    id: 'edit-project-tasks',
    link_text: 'Tasks',
    path: 'tasks/*',
    icon: ICONS.CHECKLIST,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project tasks." />,
    element: (
      <MainContentWrapper>
        <ComingSoon label="Tasks" />
      </MainContentWrapper>
    ),
    notAvailable: true,
  },
  {
    id: 'edit-project-creative-boards',
    link_text: 'Creative Boards',
    path: 'creative-boards/*',
    icon: ICONS.CREATIVE_BOARD,
    ErrorBoundary: () => (
      <EditProjectErrorBoundary label="Oops! There was an error loading your project creative boards." />
    ),
    element: (
      <MainContentWrapper>
        <ComingSoon label="Creative Boards" />
      </MainContentWrapper>
    ),
    notAvailable: true,
  },
  {
    id: 'edit-project-stylepacks',
    link_text: 'Stylepacks',
    path: 'stylepacks/*',
    icon: ICONS.STYLEPACKS,
    loader: stylepacksLoader,
    shouldRevalidate: () => false,
    ErrorBoundary: () => (
      <EditProjectErrorBoundary label="Oops! There was an error loading your project style packs." />
    ),
    element: <Outlet />,
    children: [
      {
        index: true,
        id: 'stylepacks',
        element: (
          <MainContentWrapper>
            <Stylepacks />
          </MainContentWrapper>
        ),
      },
      {
        path: 'browse/*',
        id: 'stylepacks/browse',
        element: <BrowseStylepacks />,
      },
      {
        path: 'preview/:stylepackId',
        id: 'preview-stylepack',
        element: <PreviewStylepack />,
      },
    ],
  },
  {
    // This will href replace to moderator
    id: 'edit-project-moderator',
    link_text: 'Moderator',
    icon: ICONS.MODERATOR_2,
  },
  {
    id: 'edit-project-greenroom',
    link_text: 'Greenroom®',
    icon: ICONS.GREENROOM,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project greenroom." />,
    element: (
      <MainContentWrapper>
        <ComingSoon label="Greenroom®" />
      </MainContentWrapper>
    ),
  },
  {
    id: 'edit-project-content',
    link_text: 'Content',
    path: 'content/*',
    icon: ICONS.VIDEO,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project content." />,
    element: (
      <MainContentWrapper>
        <ComingSoon label="Content" />
      </MainContentWrapper>
    ),
    notAvailable: true,
  },
  {
    // External link opened via onClick
    id: 'edit-project-live-monitor',
    link_text: 'Live Monitor',
    icon: ICONS.LIVE_SIGNAL,
  },
  {
    id: 'edit-project-segments',
    link_text: 'Segments',
    path: 'segments/*',
    icon: ICONS.SEGMENTS,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project segments." />,
    async lazy() {
      const Segments = await import('../grid-areas/main/segments/segments.tsx');
      return {
        Component: () => (
          <MainContentWrapper>
            <Segments.default />
          </MainContentWrapper>
        ),
      };
    },
  },
  {
    id: 'edit-project-run-of-show',
    link_text: 'Run of Show',
    path: 'run-of-show/*',
    icon: ICONS.RUN_OF_SHOW,
    ErrorBoundary: () => (
      <EditProjectErrorBoundary label="Oops! There was an error loading your project run of show." />
    ),
    element: (
      <MainContentWrapper>
        <ComingSoon label="Run of Show" />
      </MainContentWrapper>
    ),
    notAvailable: true,
  },
  {
    id: 'edit-project-scripts',
    link_text: 'Scripts',
    path: 'scripts/*',
    icon: ICONS.SCRIPTS,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project scripts." />,
    element: (
      <MainContentWrapper>
        <ComingSoon label="Scripts" />
      </MainContentWrapper>
    ),
    notAvailable: true,
  },
  {
    id: 'edit-project-slides',
    link_text: 'Slides',
    path: 'slides/*',
    icon: ICONS.MEDIA,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project slides." />,
    element: (
      <MainContentWrapper>
        <ComingSoon label="Slides" />
      </MainContentWrapper>
    ),
    notAvailable: true,
  },
  {
    id: 'edit-project-audiences',
    path: 'audiences',
    link_text: 'Audiences',
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project audiences." />,
    icon: ICONS.USERS_AUDIENCE,
    element: <AudiencesRouteGate />,
    children: [
      {
        index: true,
        element: <Navigate to={AUDIENCES_VIEW.LISTS} replace />,
      },
      {
        path: 'lists',
        id: 'edit-project-audiences-tabs-lists',
        ErrorBoundary: () => (
          <EditProjectErrorBoundary label="Oops! There was an error loading your project audiences." />
        ),
        element: <Audiences />,
      },
      {
        path: 'registrants',
        id: 'edit-project-registrants-list',
        ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading registrant list." />,
        element: (
          <MainContentWrapper>
            <Registrants />
          </MainContentWrapper>
        ),
      },
      {
        path: 'list/:passcodeListId',
        id: 'edit-project-audiences-list',
        ErrorBoundary: () => (
          <EditProjectErrorBoundary label="Oops! There was an error loading your project audience list." />
        ),
        element: (
          <MainContentWrapper>
            <AudiencesList />
          </MainContentWrapper>
        ),
      },
      {
        path: ':listType',
        id: 'edit-project-users-list',
        ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading registrant list." />,
        element: (
          <MainContentWrapper>
            <AllProfiles />
          </MainContentWrapper>
        ),
      },
    ],
  },
  {
    id: 'edit-project-campaigns',
    link_text: 'Campaigns',
    path: 'campaigns/*',
    icon: ICONS.AI_SEND_MESSAGE,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project campaigns." />,
    element: <Outlet />,
    ...ROUTE_PERMISSIONS.developer,
    children: [
      {
        index: true,
        id: 'campaigns-list',
        element: (
          <MainContentWrapper>
            <Campaigns />
          </MainContentWrapper>
        ),
      },
      {
        path: ':campaignUuid/edit',
        id: 'campaign-editor',
        element: <CampaignEditor />,
      },
    ],
  },
  // {
  //   id: 'edit-project-stats',
  //   link_text: 'Stats',
  //   path: 'stats/*',
  //   icon: ICONS.STATS,
  //   ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project stats." />,
  //   shouldRevalidate: () => false,
  //   async lazy() {
  //     const CsvReportAlerts = await import('../grid-areas/main/analytics/csvs/csv-report-alerts.tsx');
  //     return {
  //       Component: () => (
  //         <RouteGuard
  //           allowedRoles={ROUTE_PERMISSIONS.adminOnly.allowedRoles}
  //           unauthorizedMessage={ROUTE_PERMISSIONS.adminOnly.unauthorizedMessage}
  //           redirectTo={ROUTE_PERMISSIONS.adminOnly.redirectTo}
  //         >
  //           <CsvReportAlerts.default />
  //           <StatsCleanupWrapper>
  //             <Outlet />
  //           </StatsCleanupWrapper>
  //         </RouteGuard>
  //       ),
  //     };
  //   },
  //   children: [
  //     {
  //       id: 'project-stats',
  //       index: true,
  //       async lazy() {
  //         const ProjectAnalytics = await import(
  //           '../grid-areas/main/analytics/project-analytics/project-analytics-container.tsx'
  //         );
  //         return {
  //           Component: () => (
  //             <MainContentWrapper>
  //               <ProjectAnalytics.default />
  //             </MainContentWrapper>
  //           ),
  //         };
  //       },
  //     },
  //     {
  //       id: 'session-stats',
  //       path: 'session/:sessionUuid',
  //       shouldRevalidate: () => false,
  //       async lazy() {
  //         const SessionAnalytics = await import('../grid-areas/main/analytics/session-analytics/session-analytics.tsx');
  //         return {
  //           Component: () => (
  //             <MainContentWrapper>
  //               <SessionAnalytics.default />
  //             </MainContentWrapper>
  //           ),
  //         };
  //       },
  //     },
  //   ],
  // },
  {
    id: 'edit-project-settings',
    link_text: 'Settings',
    path: 'settings/*',
    icon: ICONS.SETTINGS,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project settings." />,
    element: (
      <MainContentWrapper>
        <Settings />
      </MainContentWrapper>
    ),
  },
  {
    id: 'edit-project-preview-template',
    path: 'project-preview/*',
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project preview." />,
    shouldRevalidate: () => false,
    link_text: '',
    async lazy() {
      const PreviewProject = await import('../../preview-project/preview-project.view.tsx');
      return { Component: PreviewProject.default };
    },
  },
];

export const EDIT_PROJECT_ROUTES_CONFIG_MAP: { [key: string]: EditProjectRouteConfig } =
  EDIT_PROJECT_ROUTES_CONFIG.reduce((acc, cur) => ({ ...acc, [cur.id]: cur }), {});

// Helper function to convert config to RouteObject
export const convertConfigToRouteObject = (config: EditProjectRouteConfig): RouteObject => {
  const componentKey = config?.Component ? 'Component' : 'element';

  // Wrap element with RouteGuard if allowedRoles is specified
  const wrappedElement = config.allowedRoles ? (
    <RouteGuard
      allowedRoles={config.allowedRoles}
      blockedRoles={config.blockedRoles}
      unauthorizedMessage={config.unauthorizedMessage}
      redirectTo={config.redirectTo}
    >
      {config.element}
    </RouteGuard>
  ) : (
    config[componentKey]
  );

  if (config.isIndex) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { children, ...rest } = config;
    const routeObject: RouteObject = {
      ...rest,
      index: true,
      [componentKey]: config?.lazy ? undefined : wrappedElement,
    };
    return routeObject;
  } else {
    const routeObject: RouteObject = {
      ...config,
      [componentKey]: config?.lazy ? undefined : wrappedElement,
    };

    return routeObject;
  }
};
